import Immutable from 'seamless-immutable';
import { uniqueId } from 'lodash';
import voca from 'voca';
import shallowEqual from 'fbjs/lib/shallowEqual';
import { getLogger } from '@cld/upload-widget';
import createReducer from 'common/reducerBase';
import { spliceArrayProp, pushToArrayProp, withoutIn } from 'utils/seamlessUtils';
import { getFileNameFromUrl } from '../common/utils';
import parseConfig, { configMerger } from '../configParser';
import consts from '../common/consts';
import UploaderRecord, { createUploadQueue, createFileData } from './UploderRecord';
import { types as ats } from './actions';

// todo: add config version instead of "sameConfig" boolean
// todo: on retries combine files into same queue in case same config version is detected

const logger = getLogger();

const CLD_CONSOLE_RGX = /(dev|demo)?\.?cloudinary\.com$/;
const CLD_PROGRAMMABLE_MEDIA_RGX = /^\/pm(?:\/|$)/;
const SECURE_URL_RGX = /^https:\/\//;

// get the first queue that isnt running or finished
const getFirstActiveQueueIndx = (state) => state.queues.findIndex((q) => q.status === consts.QUEUE_STATUS.NONE);

const getRunningQueueIndx = (state) => state.queues.findIndex((q) => q.status === consts.QUEUE_STATUS.RUNNING);

const getQueueIndxForFileId = (id, state) => {
  const indx = state.fileQueueIndex[id];

  if (!~indx) {
    throw new Error(`UPLOADER.REDUCER - impossible state transition! no queue in store contains file with id: ${id}`);
  }

  return indx;
};

const getQueueIndxForQueueId = (id, state) => state.queues.findIndex((q) => q.id === id);

const createFileDetails = (fileId, fileData, delayedPreCalls) => {
  const { file } = fileData;

  let size = 0;
  let type = '';
  let name = null;

  const isFetch = voca.isString(file) || voca.isString(file.file);

  if (voca.isString(file)) {
    name = getFileNameFromUrl(file);
  } else {
    name = file.name;
    size = file.size;
    type = file.type;
  }

  return createFileData({
    id: fileId,
    name,
    size,
    type,
    headers: file.headers,
    filenameOverride: file.filenameOverride,
    folder: fileData.folder || file.folder,
    partOfBatch: fileData.partOfBatch,
    paused: fileData.delayed,
    batchId: fileData.batchId,
    publicId: fileData.publicId,
    publicIdCounter: fileData.publicIdCounter,
    camera: !!file.camera,
    customCoordinates: fileData.customCoordinates,
    imageDimensions: fileData.imageDimensions,
    delayedPreCalls,
    isFetch,
    fetchImageDimensions: file.fetchImageDimensions,
  });
};

const getPreviewUrl = (url, queueConfig) => {
  let previewUrl = url;

  if (!SECURE_URL_RGX.test(url)) {
    // dont allow insecure urls unless showInsecurePreview is true
    previewUrl = queueConfig.showInsecurePreview === true ? previewUrl : null;
  }

  return previewUrl;
};

const createFileOperations = (file, queueConfig) => {
  const getPreviewObject = () =>
    voca.isString(file)
      ? {
          url: getPreviewUrl(file, queueConfig),
        }
      : {
          preview: file.preview,
          url: file.thumbnail || getPreviewUrl(file.file, queueConfig), // in case of custom file data
        };

  const getFileObject = () => (voca.isString(file) ? file : file.file || file); // in case of custom file data

  return {
    getPreviewObject,
    getFileObject,
  };
};

const insertNewFileToQueue = (state, id, fileDetails, fileOperations, delayed, queueIndx) => {
  let queue = state.queues[queueIndx];

  queue = queue.setIn(['filesData', id], fileDetails);
  queue = queue.setIn(['files', id], fileOperations);
  queue = queue.set('pendingPreCalls', delayed);
  queue = pushToArrayProp(queue, 'ids', id);

  return state.setIn(['queues', queueIndx], queue);
};

const getStatusText = (data, existingStatus) => {
  const { status } = data;
  let text = existingStatus;

  if (status) {
    if (voca.isString(status) && status !== consts.ABORTED) {
      text = status;
    } else if (status.error && status.error.message) {
      text = status.error.message;
    }
  }

  return text;
};

const setFileData = (state, id, data, queueIndx = null) => {
  queueIndx = queueIndx || getQueueIndxForFileId(id, state);

  let queue = state.queues[queueIndx];

  if (queue) {
    queue = queue.merge(
      {
        // merge the new data with existing file data
        filesData: {
          [id]: {
            ...data,
            statusText: getStatusText(data, queue.filesData[id].statusText),
          },
        },
      },
      { deep: true }
    );

    state = spliceArrayProp(state, ['queues'], queueIndx, 1, queue);
  } else {
    logger.log(`[uploader.reducer]: setFileData - couldnt find queue for file id: ${id}`, data);
  }

  return state;
};

const setFileGetPreviewMethod = (state, queueIndx, fileId, newMethod) =>
  state.setIn(['queues', queueIndx, 'files', fileId, 'getPreviewObject'], newMethod);

const setFileDataWithVersionIncrement = (state, id, data, queueIndx = null) => {
  state = setFileData(state, id, data, queueIndx);

  // increment version for file so it easy for subscribed consumers to know a change to the file was made
  return state.setIn(['fileVersions', id], state.fileVersions[id] + 1);
};

const setFileDataMethods = (state, id, methods) => {
  const queueIndx = getQueueIndxForFileId(id, state);

  const queue = state.queues[queueIndx].merge(
    {
      files: {
        [id]: methods,
      },
    },
    { deep: true }
  );

  return spliceArrayProp(state, ['queues'], queueIndx, 1, queue);
};

const createQueueFromPreviousQueue = (previousQueue, props = {}) =>
  createUploadQueue({
    sameConfig: !!(!props.config && previousQueue && previousQueue.queueId),
    config: previousQueue ? previousQueue.config : null,
    ...props,
  });

const removeFileFromQueue = (state, fileId, queueIndx, queue) => {
  queue = spliceArrayProp(queue, 'ids', queue.ids.indexOf(fileId), 1) // remove from the ids array
    .asMutable();

  queue.files = Immutable.without(queue.files, fileId); // remove the file object
  queue.filesData = Immutable.without(queue.filesData, fileId); // remove the file data object

  state = spliceArrayProp(state, ['queues'], queueIndx, 1, queue);

  return withoutIn(state, 'fileQueueIndex', fileId);
};

const addUploadFileToQueue = (payloadFile, payload, queueIndex, state) => {
  const fileId = payloadFile.id;
  const fileDetails = createFileDetails(fileId, payloadFile, payload.delayedPreCalls);

  queueIndex = ~queueIndex ? queueIndex : getFirstActiveQueueIndx(state);

  if (!~queueIndex) {
    const queuesCount = state.queues.length;

    const prevQueue = state.queues[queuesCount - 1];

    if (!prevQueue) {
      throw new Error('UPLOADER.REDUCER - impossible state transition! no queue in store when adding a new file');
    }

    // create new queue from existing queue or from config received from action
    state = pushToArrayProp(
      state,
      'queues',
      createQueueFromPreviousQueue(prevQueue, {
        filesData: { [fileId]: fileDetails },
        files: { [fileId]: createFileOperations(payloadFile.file, prevQueue.config) },
        ids: [fileId],
        pendingPreCalls: payload.delayed,
      })
    );

    queueIndex = queuesCount; // new queue is last in state.queues
  } else {
    // push to next active queue
    state = insertNewFileToQueue(
      state,
      fileId,
      fileDetails,
      createFileOperations(payloadFile.file, state.queues[queueIndex].config),
      payload.delayed,
      queueIndex
    );
  }

  return state
    .setIn(['fileQueueIndex', fileId], queueIndex) // update the queue index for the file
    .setIn(['fileVersions', fileId], 0); // initialize the version for the file
};

const resetStatusDataForRetry = (fileData) => ({
  ...fileData,
  status: null,
  progress: 0,
  done: false,
  failed: false,
  aborted: false,
  paused: false,
});

const processQueueRetries = (state, queueIndx, fileIds) => {
  const queuesCount = state.queues.length;
  const filesData = {};
  const files = {};
  const ids = [];
  const fileQueueIndex = {};
  const fileVersions = {};

  fileIds.forEach((id) => {
    const queue = state.queues[queueIndx]; // fetch the latest queue state on each iteration

    filesData[id] = resetStatusDataForRetry(queue.filesData[id]);
    files[id] = queue.files[id];
    ids.push(id);
    fileQueueIndex[id] = queuesCount;
    fileVersions[id] = 0;

    state = removeFileFromQueue(state, id, queueIndx, queue);
  });

  const queue = state.queues[queueIndx]; // get the latest queue state

  state = pushToArrayProp(
    state,
    'queues', // create a new queue based on the queues the retried files came from
    createQueueFromPreviousQueue(queue, {
      filesData,
      files,
      ids,
      metadata: queue?.metadata,
      //mark as retry queue, not to be used as base for new queue
      retryQueue: true,
    })
  );

  return state
    .merge({ fileQueueIndex }, { deep: true }) // update the queue index mapping for the retried files
    .merge({ fileVersions }, { deep: true }); // reset the versions for the retried files
};

// remove file completely from the store - should only be used for cancelling batch due to pre-batch flow cancelling
const removeFileFromStore = (state, fileId, queueIndx, queue) => {
  state = removeFileFromQueue(state, fileId, queueIndx, queue);
  return withoutIn(state, 'fileVersions', fileId);
};

const getBasePublicId = (publicId, counter) => publicId.substr(0, publicId.length - (1 + counter.toString().length));

const rewindPublicIdCounter = (state, files, queueIndx) => {
  const firstFileData = state.queues[queueIndx].filesData[files[0].id];
  const firstPublicIdCounter = firstFileData.publicIdCounter;

  let basePublicId;

  if (~firstPublicIdCounter) {
    // batch had public id set if the counter isnt set to -1
    if (firstPublicIdCounter) {
      basePublicId = getBasePublicId(firstFileData.publicId, firstPublicIdCounter); // remove the suffix (dash + the number)
    } else {
      // 0 so was the first one, no suffix
      basePublicId = firstFileData.publicId;
    }

    state = state.setIn(['publicIdCounters', basePublicId], firstPublicIdCounter); // rewind to the first cancelled file's counter
  }

  return state;
};

const shouldUseServerUrl = (result, state, queueIndx, fileId) => {
  let shouldUse = false;

  if (result && result.secure_url && result.resource_type === 'image' && !/.pdf$/.test(result.secure_url)) {
    const preview = state.queues[queueIndx].files[fileId].getPreviewObject();

    shouldUse = !preview || (!preview.preview && !preview.img);
  }

  return shouldUse;
};

const setNewUploadPublicId = (id, state) => (id === '' || (id && state.uploadPublicId !== id) ? state.set('uploadPublicId', id || '') : state);

const setNewUploadTags = (tags, state) => (tags && !shallowEqual(state.uploadTags, tags) ? state.set('uploadTags', tags) : state);

const toggleClearQueuesInProgress = (inProgress, state) => state.set('clearQueuesInProgress', inProgress);

const extractDimensions = (data) => (data.preview && data.preview.width ? [data.preview.width, data.preview.height] : [data.img.width, data.img.height]);

const getPreviousIndexForUpdate = (state) =>
  state.queues.indexOf(
    state.queues
      //get new array coz reverse changes array (cant call on immutable)
      .asMutable()
      //reverse to get most recent queue created
      .reverse()
      //get queue that wasnt created for retry
      .find((queue) => !queue.retryQueue)
  );

export default createReducer(UploaderRecord(), {
  [ats.SET_UPLOADER_CONFIG]: (state, { payload }) => {
    const queuesCount = state.queues.length;
    const id = state.id || payload.config.widgetId || uniqueId('uw');
    const { showOnStart } = payload.config;
    const qsParams = !queuesCount ? state.qsParams : {}; // only use qsparams the first time!

    let config = configMerger({}, payload.config, qsParams);

    const previousQueueIndex = getPreviousIndexForUpdate(state),
      previousQueue = state.queues[previousQueueIndex];

    if (previousQueue) {
      // inherit existing config from last queue and merge new props on top
      config = configMerger({}, previousQueue.config, config);
    }

    // apply filtering to only allowed config and config initialization logic
    config = parseConfig(config);

    // create new queue if no queues yet or previous queue already has files
    if (!previousQueue || previousQueue.ids.length) {
      state = pushToArrayProp(state, 'queues', createQueueFromPreviousQueue(previousQueue, { config }));
    } else {
      // set on current queue
      state = state.setIn(['queues', previousQueueIndex, 'config'], config);
    }

    state = setNewUploadPublicId(config.publicId, state); // make sure the config's public id is used in case its provided
    state = setNewUploadTags(config.tags, state); // make sure the config's tags are used in case provided

    return state.merge({
      id,
      showOnStart,
    });
  },

  [ats.SET_UPLOADER_CLOUD_INFO]: (state, { payload }) =>
    state.set('cloudInfo', {
      cloudName: payload.cloudName,
      ...payload.info,
    }),

  [ats.SET_UPLOADER_QS_PARAMS]: (state, { payload }) => {
    let cldMatch = payload.params.pmHost.match(CLD_CONSOLE_RGX);

    if (cldMatch && cldMatch[1] === 'demo') {
      // if we're on demo.cloudinary.com then we're not in console
      cldMatch = null;
    }

    return state.merge({
      qsParams: payload.params,
      onConsole: !!cldMatch,
      onDevConsole: !!(cldMatch && cldMatch[1]),
      onProgrammableMedia: !!payload.params.pmPath?.match(CLD_PROGRAMMABLE_MEDIA_RGX),
    });
  },

  [ats.SET_UPLOADER_CURRENT_SOURCE]: (state, { payload }) => state.set('source', payload.source),

  [ats.SET_UPLOADER_FILES_QUEUED]: (state, { payload }) => {
    let queueIndex = -1;

    if (payload.configOverride) {
      queueIndex = getFirstActiveQueueIndx(state);

      if (~queueIndex && !state.queues[queueIndex].ids.length) {
        // queue is ready and not containing any files
        state = state.setIn(['queues', queueIndex], {
          ...state.queues[queueIndex],
          configOverride: payload.configOverride,
          pendingPreCalls: payload.delayed,
        });
      } else {
        // create queue with the action's config so the new files are added to it
        state = pushToArrayProp(state, 'queues', createQueueFromPreviousQueue({ config: payload.configOverride }, { pendingPreCalls: payload.delayed }));

        queueIndex = state.queues.length - 1;
      }
    }

    payload.files.forEach((f) => {
      state = addUploadFileToQueue(f, payload, queueIndex, state);
    });

    return state;
  },

  [ats.SET_UPLOADER_BATCH_PREPARED]: (state, { payload }) => {
    Object.keys(payload.files).forEach((fileId) => {
      state = setFileData(state, fileId, {
        preparedParams: payload.files[fileId],
      });
    });

    return state;
  },

  [ats.SET_UPLOADER_SHOW]: (state, { payload }) =>
    state.merge({
      showCounter: state.showCounter + 1,
      showOptions: payload ? payload.options : null,
      source: (payload && payload.source) || state.source,
    }),

  [ats.SET_UPLOADER_MINIMIZE]: (state) => state.set('minimizeCounter', state.minimizeCounter + 1),

  [ats.SET_UPLOADER_QUEUE_RUNNING_FOR_FILE]: (state, { payload }) => {
    const runningIndx = getRunningQueueIndx(state);

    if (~runningIndx) {
      if (!~state.queues[runningIndx].ids.indexOf(payload.id)) {
        throw new Error(`UPLOADER.REDUCER - impossible state transition!
				cant set more than one running queue at a time. file id = ${payload.id}`);
      }
    } else {
      const queueIndx = getQueueIndxForFileId(payload.id, state);
      state = state.setIn(['queues', queueIndx, 'status'], consts.QUEUE_STATUS.RUNNING);
    }

    return state;
  },

  [ats.SET_UPLOADER_FILE_DONE]: (state, { payload }) => {
    const queueIndx = getQueueIndxForFileId(payload.id, state);

    state = setFileDataWithVersionIncrement(state, payload.id, {
      status: payload.success ? 'success' : payload.result,
      uploadInfo: payload.success ? payload.result : null,
      aborted: payload.result === consts.ABORTED,
      failed: !payload.success,
      timeout: payload.result === 'timeout',
      done: true,
      paused: false,
    });

    if (shouldUseServerUrl(payload.result, state, queueIndx, payload.id)) {
      state = setFileGetPreviewMethod(state, queueIndx, payload.id, () => ({ url: payload.result.secure_url }));
    }

    const queue = state.queues[queueIndx];

    if (!Object.values(queue.filesData).find((data) => !data.done)) {
      // if all files are done, update queue status to finished
      state = state.setIn(['queues', queueIndx, 'status'], consts.QUEUE_STATUS.FINISHED);
    }

    if (payload.success) {
      state = state.set('successCounter', state.successCounter + 1);
    }

    return state;
  },

  [ats.SET_UPLOADER_FILE_PROGRESS]: (state, { payload }) =>
    setFileDataWithVersionIncrement(state, payload.id, {
      progress: payload.progress,
      paused: false,
      ...(payload.progress === 100 && { uploadedAt: Date.now() }),
    }),

  [ats.SET_UPLOADER_HIDE]: (state, { payload }) =>
    state.merge({
      hideOptions: payload.options || null,
      hideCounter: state.hideCounter + 1,
    }),

  [ats.CLEAR_UPLOADER_QUEUES]: (state) => {
    const lastQueue = state.queues[state.queues.length - 1];

    const lastQueueConfig = lastQueue && (lastQueue.configOverride || lastQueue.config);

    return state.merge({
      queues: [createQueueFromPreviousQueue(lastQueue)], // replace all queues with a new one
      fileQueueIndex: {},
      fileVersions: {},
      publicIdCounters: {},
      successCounter: 0,
      uploadPublicId: lastQueueConfig?.publicId || null,
      uploadTags: lastQueueConfig?.tags || [],
      uploadPreset: null,
    });
  },

  [ats.SET_UPLOADER_RETRIEVED_TAGS]: (state, { payload }) => state.set('retrievedTags', payload.tags || []),

  [ats.SET_UPLOADER_RETRIEVED_UPLOAD_PRESETS]: (state, { payload }) => state.set('retrievedUploadPresets', payload.uploadPresets || []),

  [ats.SET_UPLOADER_PUBLICID]: (state, { payload }) => {
    const { id, cleanAfterUpload } = payload;

    const cleanIndex = state.postBatchCleanList.indexOf('uploadPublicId'),
      setToClean = cleanIndex !== -1;

    state = setNewUploadPublicId(id, state);

    if (setToClean !== !!cleanAfterUpload) {
      state = setToClean ? spliceArrayProp(state, 'postBatchCleanList', 'uploadPublicId') : pushToArrayProp(state, 'postBatchCleanList', 'uploadPublicId');
    }

    return state;
  },

  [ats.SET_UPLOADER_TAGS]: (state, { payload }) => setNewUploadTags(payload.tags, state),

  [ats.SET_UPLOADER_UPLOAD_PRESET]: (state, { payload }) => state.set('uploadPreset', payload.uploadPreset),

  [ats.SET_UPLOADER_QUEUE_FINISHED]: (state, { payload }) => {
    const queueIndx = getQueueIndxForQueueId(payload.id, state);

    return ~queueIndx ? state.setIn(['queues', queueIndx, 'status'], consts.QUEUE_STATUS.FINISHED) : state;
  },

  [ats.SET_UPLOADER_RETRY_FILES]: (state, { payload }) => {
    const retriesInQueues = Object.keys(state.fileQueueIndex) // hold file ids to retry per queue
      .reduce((res, id) => {
        if (payload.retryAll || ~payload.ids.indexOf(id)) {
          // file id should be specified in payload unless retry all is set to true
          const queueIndx = state.fileQueueIndex[id];

          if (state.queues[queueIndx].filesData[id].failed) {
            // only take failed files
            res[queueIndx] = res[queueIndx] ? res[queueIndx].concat(id) : [id];
          }
        }

        return res;
      }, {});

    Object.keys(retriesInQueues).forEach((qIndx) => {
      state = processQueueRetries(state, parseInt(qIndx), retriesInQueues[qIndx]);
    });

    return state;
  },

  [ats.SET_UPLOADER_START_CLIENT_BATCH]: (state, { payload }) => {
    if (payload.batchId) {
      state = pushToArrayProp(state, 'clientUploadBatches', {
        id: payload.batchId,
        files: [],
        batchOptions: payload.batchOptions,
        count: payload.count,
        // must store config for later use because file processing on the client side
        // may end after config already changed - we dont allow to change config for this batch
        // during processing even if that's the developer's desire
        config: payload.config,
      });
    } else {
      logger.warn('[uploader.reducer]: client batch received without batchId!');
    }

    return state;
  },

  /**
   * in charge of updating the clientUploadBatches state
   * for uploads coming from the client - externally. ex: d&d outside UW
   */
  [ats.SET_UPLOADER_ADD_CLIENT_UPLOAD]: (state, { payload }) => {
    if (payload.batchId) {
      const batchIndex = state.clientUploadBatches.findIndex((b) => b.id === payload.batchId);

      if (~batchIndex) {
        state = pushToArrayProp(state, ['clientUploadBatches', batchIndex, 'files'], payload.file);
      } else {
        logger.warn(`[uploader.reducer]: received file for batch ${payload.batchId} that doesnt exist`);
      }
    } else {
      logger.warn('[uploader.reducer]: client batch received without batchId!');
    }

    return state;
  },

  [ats.CLEAR_UPLOADER_CLIENT_BATCH]: (state) => spliceArrayProp(state, 'clientUploadBatches', 0),

  [ats.SET_UPLOADER_FILE_PAUSED]: (state, { payload }) => setFileDataWithVersionIncrement(state, payload.id, { paused: true }),

  [ats.SET_UPLOADER_FILE_DATA_METHODS]: (state, { payload }) => setFileDataMethods(state, payload.id, payload.methods),

  [ats.SET_UPLOADER_FILE_PREVIEW]: (state, { payload }) => {
    const fileId = payload.id;

    const queueIndx = getQueueIndxForFileId(fileId, state);

    if (payload.preview || payload.img) {
      state = setFileDataWithVersionIncrement(
        state,
        fileId,
        {
          dimensions: extractDimensions(payload),
        },
        queueIndx
      );
    }

    if (payload.img) {
      const preview = {
        img: payload.img,
      };

      state = setFileGetPreviewMethod(state, queueIndx, fileId, () => preview); // replace getPreviewObject file op with updated version
    }

    return state;
  },

  [ats.SET_UPLOADER_PID_COUNTER]: (state, { payload }) => state.setIn(['publicIdCounters', payload.publicId], payload.counter),

  [ats.SET_UPLOADER_QUEUE_PRECALLS_PENDING]: (state, { payload }) => {
    const qIndx = getQueueIndxForFileId(payload.files[0].id, state);

    if (!payload.pending && state.queues[qIndx].pendingPreCalls) {
      // when changing from pending to not
      const queueFileIds = state.queues[qIndx].ids;
      const versions = state.fileVersions;

      const bumpedVersions = queueFileIds.reduce((res, id) => {
        res[id] = versions[id] + 1;
        return res;
      }, {});

      // bump the versions of the files so they are notified about as changed/started
      state = state.merge({ fileVersions: bumpedVersions }, { deep: true });
    }

    return state.setIn(['queues', qIndx, 'pendingPreCalls'], payload.pending);
  },

  [ats.CANCEL_UPLOADER_FILES]: (state, { payload }) => {
    const qIndx = getQueueIndxForFileId(payload.files[0].id, state);

    // rewind the public id counter (if public id was set for this batch) so it isnt incremented by the cancelled uploads
    state = rewindPublicIdCounter(state, payload.files, qIndx);

    payload.files.forEach((f) => {
      state = removeFileFromStore(state, f.id, qIndx, state.queues[qIndx]);
    });

    return state;
  },

  [ats.START_CLEAR_QUEUES]: (state) => toggleClearQueuesInProgress(true, state),

  [ats.END_CLEAR_QUEUES]: (state) => toggleClearQueuesInProgress(false, state),

  [ats.SET_UPLOADER_FILE_COORDINATES]: (state, { payload }) =>
    setFileDataWithVersionIncrement(state, payload.id, {
      customCoordinates: payload.customCoordinates,
      coordinatesResize: payload.coordinatesResize,
    }),

  [ats.SET_UPLOADER_FILE_PRECALLS_DELAYED_OFF]: (state, { payload }) => {
    payload.ids.forEach((id) => {
      // dont increment file data version for this update!
      state = setFileData(state, id, { delayedPreCalls: false });
    });

    return state;
  },

  [ats.SET_UPLOADER_QUEUE_METADATA]: (state, { payload }) => {
    const qIndx = getQueueIndxForFileId(payload.files[0].id, state);

    const stateMetadata =
      payload.metadata !== consts.NO_METADATA_REQUIRED
        ? {
            values: payload.metadata,
            conflicts: payload.conflicts,
            fields: payload.fields,
          }
        : null;

    return state.setIn(['queues', qIndx, 'metadata'], stateMetadata);
  },

  [ats.CLEAN_POST_BATCH_PROP_LIST]: (state) => {
    state.postBatchCleanList.forEach((prop) => {
      state = state.set(prop, null);
    });

    return state.set('postBatchCleanList', []);
  },
});
